import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/** Focusable elements a dialog's Tab trap cycles through, unless a dialog passes its own selector. */
export const DIALOG_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab and Shift+Tab inside `focusable` by wrapping from the last element to the
 * first and back. When nothing has focus (the page body), Tab moves into the list instead
 * of onto the page behind it. Returns true when it moved focus.
 */
export function wrapTabFocus(event: KeyboardEvent, focusable: ArrayLike<HTMLElement>): boolean {
  if (event.key !== 'Tab' || focusable.length === 0) return false;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (!active || active === document.body) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return true;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return true;
  }
  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/**
 * Whether an element is rendered and can take focus. A hidden element would otherwise become
 * the trap's first or last target, and `.focus()` on it is a no-op that drops focus to the page.
 * A descendant of a `display:none` parent still reports its own computed `display`, so browsers
 * that implement `checkVisibility` use it to catch hidden ancestors. Where it is missing (older
 * Safari, jsdom) the element and every ancestor are checked for `display:none`, plus the
 * element's own `visibility` (which already inherits). Only the element's own `aria-hidden`
 * counts: apps commonly set `aria-hidden` on their root while another modal is open, and that
 * must not switch off dialogs rendered inside the root.
 */
export function isVisible(el: HTMLElement): boolean {
  if (el.closest('[hidden]') || el.getAttribute('aria-hidden') === 'true') return false;
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ visibilityProperty: true });
  }
  if (typeof getComputedStyle !== 'function') return true;
  if (getComputedStyle(el).visibility === 'hidden') return false;
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

/** One entry per open dialog, newest last, so stacked dialogs can tell which one is on top. */
const openDialogs: { containerRef: RefObject<HTMLElement | null> }[] = [];

/**
 * Whether `entry` is the top-most open dialog the user can see. An open but hidden dialog (a
 * desktop copy inside a region hidden at mobile width) is skipped so it cannot shadow the
 * visible one underneath it.
 */
const isTopVisibleDialog = (entry: (typeof openDialogs)[number]): boolean => {
  for (let i = openDialogs.length - 1; i >= 0; i -= 1) {
    const container = openDialogs[i].containerRef.current;
    if (container && isVisible(container)) return openDialogs[i] === entry;
  }
  return false;
};

/**
 * Whether a `useDialog` dialog the user can see is open. Overlays that handle their own keys
 * outside this stack (the MegaMenu mobile drawer) use it to stand down while a dialog sits on
 * top of them, so one Escape or Tab is not handled twice.
 */
export function hasOpenVisibleDialog(): boolean {
  return openDialogs.some(({ containerRef }) => {
    const container = containerRef.current;
    return !!container && isVisible(container);
  });
}

let scrollLockCount = 0;
let overflowBeforeLock = '';

// Counted so that nested dialogs closing in any order restore the page's own overflow, not the
// 'hidden' an inner dialog saw when it opened.
const lockPageScroll = (): void => {
  if (scrollLockCount === 0) {
    overflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount += 1;
};

const unlockPageScroll = (): void => {
  scrollLockCount -= 1;
  if (scrollLockCount === 0) document.body.style.overflow = overflowBeforeLock;
};

export interface UseDialogOptions {
  /** Whether the dialog is showing; listeners and focus handling only run while true. Default true. */
  isOpen?: boolean;
  /** The dialog element: Tab is trapped inside it and clicks outside it count as outside clicks */
  containerRef: RefObject<HTMLElement | null>;
  /** Called on Escape */
  onEscape: () => void;
  /** Called on a mousedown outside the dialog; omit to keep outside clicks from closing it */
  onOutsideClick?: () => void;
  /** Elements whose clicks are not outside clicks, such as the button that opens the dialog */
  outsideClickIgnoreRefs?: RefObject<HTMLElement | null>[];
  /** Element focused when the dialog opens, usually its close button */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Return focus to the element that had it before the dialog opened, once it closes. Default false. */
  restoreFocus?: boolean;
  /**
   * Element to return focus to on close, instead of whatever had focus when the dialog opened.
   * Needed where a click does not focus the button that opened the dialog (macOS Safari).
   * Only used when `restoreFocus` is true.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Stop the page behind the dialog from scrolling while it is open. Default false. */
  lockScroll?: boolean;
  /** Elements the Tab trap cycles through. Default DIALOG_FOCUSABLE_SELECTOR. */
  focusableSelector?: string;
}

/**
 * Shared dialog behaviour: Escape, Tab focus trap, initial focus, optional outside-click
 * dismissal, focus restore and page scroll lock. Every popup in the library uses this so
 * they behave the same and are tested once.
 */
export function useDialog({
  isOpen = true,
  containerRef,
  onEscape,
  onOutsideClick,
  outsideClickIgnoreRefs,
  initialFocusRef,
  restoreFocus = false,
  returnFocusRef,
  lockScroll = false,
  focusableSelector = DIALOG_FOCUSABLE_SELECTOR,
}: UseDialogOptions): void {
  const latest = useRef({ onEscape, onOutsideClick, outsideClickIgnoreRefs, focusableSelector });
  latest.current = { onEscape, onOutsideClick, outsideClickIgnoreRefs, focusableSelector };

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    initialFocusRef?.current?.focus();

    const entry = { containerRef };
    openDialogs.push(entry);
    if (lockScroll) lockPageScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      // Only the top-most visible dialog answers keys: a dialog underneath must not close on
      // the same Escape or pull Tab focus back into itself, and an open but hidden dialog must
      // not act on controls the user cannot see.
      if (!isTopVisibleDialog(entry)) return;
      if (event.key === 'Escape') {
        latest.current.onEscape();
        return;
      }
      if (event.key !== 'Tab' || !container) return;
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(latest.current.focusableSelector),
      ).filter(isVisible);
      const active = document.activeElement as HTMLElement | null;
      // An initial-focus target that is not itself a Tab stop (a dialog panel with tabindex -1)
      // sits outside the focusable list, so wrapTabFocus would let Shift+Tab leave the dialog.
      const onUntabbableStart = active === initialFocusRef?.current && !focusable.includes(active as HTMLElement);
      if (focusable.length > 0 && (!container.contains(active) || onUntabbableStart)) {
        event.preventDefault();
        focusable[event.shiftKey ? focusable.length - 1 : 0].focus();
        return;
      }
      wrapTabFocus(event, focusable);
    };

    const handleMouseDown = (event: MouseEvent) => {
      const { onOutsideClick: handleOutside, outsideClickIgnoreRefs: ignored } = latest.current;
      const container = containerRef.current;
      const target = event.target as Node;
      // A click inside a dialog stacked on top is outside this one, and must not dismiss it.
      if (!handleOutside || !container || !isTopVisibleDialog(entry) || container.contains(target)) return;
      if (ignored?.some((ref) => ref.current?.contains(target))) return;
      handleOutside();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      openDialogs.splice(openDialogs.indexOf(entry), 1);
      if (lockScroll) unlockPageScroll();
      if (restoreFocus) (returnFocusRef?.current ?? previouslyFocused)?.focus();
    };
  }, [isOpen, containerRef, initialFocusRef, restoreFocus, returnFocusRef, lockScroll]);
}
